/**
 * Вход и регистрация: стеклянная карточка на фоне улья с анимацией отправки.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { login, register } from "../api/auth.js";
import { ApiError, errorI18nKey } from "../api/client.js";
import type { Session } from "../game/session.js";
import { AuthScene } from "./auth/AuthScene.js";
import { AuthSubmitButton, type AuthSubmitPhase } from "./auth/AuthSubmitButton.js";
import "./auth/auth.css";

export interface AuthPanelProps {
  readonly defaultEmail: string | null;
  readonly onSession: (session: Session) => void;
}

type CardState = "idle" | "checking" | "ok" | "error";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function AuthPanel({
  defaultEmail,
  onSession,
}: AuthPanelProps): React.JSX.Element {
  const { t } = useTranslation(["common", "errors"]);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [cardState, setCardState] = useState<CardState>("idle");
  const [submitPhase, setSubmitPhase] = useState<AuthSubmitPhase>("idle");

  const resetFeedback = () => {
    setErrorCode(null);
    setCardState("idle");
    setSubmitPhase("idle");
  };

  const switchMode = (next: "login" | "register") => {
    setMode(next);
    resetFeedback();
  };

  const submit = async () => {
    if (cardState === "checking" || submitPhase === "walking" || submitPhase === "ok") return;

    setCardState("checking");
    setSubmitPhase("checking");
    setErrorCode(null);

    try {
      let result: { accessToken: string; refreshToken: string };

      if (mode === "register") {
        result = await register({
          email,
          password,
          nickname: nickname || email.split("@")[0] || "Player",
        });
      } else {
        result = await login({ email, password });
      }

      setSubmitPhase("walking");
      await sleep(mode === "login" ? 780 : 520);
      setSubmitPhase("ok");
      setCardState("ok");
      await sleep(640);

      onSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        email,
      });
    } catch (err) {
      setCardState("error");
      setSubmitPhase("error");
      setErrorCode(err instanceof ApiError ? err.code : "network.offline");
      await sleep(900);
      setSubmitPhase("idle");
      setCardState("idle");
    }
  };

  const busy = cardState === "checking" || submitPhase === "walking" || submitPhase === "ok";

  return (
    <AuthScene>
      <div className="auth-wrap">
        <section
          className="auth-card"
          data-state={cardState}
          aria-label={t("common:auth.title")}
        >
          <div className="auth-card__sheen" aria-hidden="true" />

          <header className="auth-card__head">
            <span className="auth-card__brand">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M12 2 4 7v10l8 5 8-5V7L12 2zm0 3.2 5.5 3.4v6.8L12 18.8 6.5 15.4V8.6L12 5.2z"
                />
              </svg>
              {t("common:app.name")}
            </span>
            <h1 className="auth-card__title">{t("common:auth.title")}</h1>
            <p className="auth-card__sub">
              {mode === "login"
                ? t("common:auth.subtitle_login")
                : t("common:auth.subtitle_register")}
            </p>
          </header>

          <div className="auth-card__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={`auth-card__tab${mode === "login" ? " auth-card__tab--active" : ""}`}
              onClick={() => switchMode("login")}
              disabled={busy}
            >
              {t("common:auth.login")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={`auth-card__tab${mode === "register" ? " auth-card__tab--active" : ""}`}
              onClick={() => switchMode("register")}
              disabled={busy}
            >
              {t("common:auth.register")}
            </button>
          </div>

          {mode === "register" && (
            <div className="auth-field">
              <input
                id="auth-nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                autoComplete="nickname"
                placeholder=" "
                disabled={busy}
              />
              <label htmlFor="auth-nickname">{t("common:auth.nickname")}</label>
            </div>
          )}

          <div className="auth-field">
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder=" "
              disabled={busy}
            />
            <label htmlFor="auth-email">{t("common:auth.email")}</label>
          </div>

          <div className="auth-field auth-field--password">
            <input
              id="auth-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              placeholder=" "
              disabled={busy}
            />
            <label htmlFor="auth-password">{t("common:auth.password")}</label>
            <button
              type="button"
              className={`auth-field__reveal${showPassword ? " auth-field__reveal--on" : ""}`}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword ? t("common:auth.hide_password") : t("common:auth.show_password")
              }
              disabled={busy}
            >
              <svg className="auth-field__icon-on" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z"
                />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              <svg className="auth-field__icon-off" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  d="M3 3l18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2M6.7 6.7C4.6 8.1 3 10 3 10s4 7 10 7c1.6 0 3-.4 4.3-1M17 14.8C19 13.4 21 11 21 11s-4-7-10-7c-1.1 0-2.1.2-3 .5"
                />
              </svg>
            </button>
          </div>

          {errorCode && (
            <p className="auth-card__error" role="alert">
              {t(errorI18nKey(errorCode), { defaultValue: errorCode })}
            </p>
          )}

          <AuthSubmitButton
            label={mode === "login" ? t("common:auth.login") : t("common:auth.register")}
            phase={submitPhase}
            disabled={busy && submitPhase !== "error"}
            onClick={() => void submit()}
          />
        </section>
      </div>
    </AuthScene>
  );
}
